import {BillingPlanPanel} from '../billing-page/billing-plan-panel';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {useCartStore} from '../shop/use-cart-store';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useState} from 'react';
import {Form} from '@ui/forms/form';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {RadioGroup} from '@ui/forms/radio-group/radio-group';
import {FormRadioGroup} from '@ui/forms/radio-group/radio-group';
import {Radio} from '@ui/forms/radio-group/radio';
import {CreditCardIcon} from '@ui/icons/material/CreditCard';
import {AccountBalanceWalletIcon} from '@ui/icons/material/AccountBalanceWallet';
import {CartItem} from '../shop/product';

export function CheckoutPage() {
  const cart = useCartStore((state: any) => state.cart);
  const clearCart = useCartStore((state: any) => state.clearCart);
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const form = useForm();

  const total = cart.reduce((sum: number, item: CartItem) => {
    const basePrice = parseFloat(item.price[0]?.price || '0');
    const price = item.localCurrencyRate || basePrice;
    return sum + (price * item.quantity);
  }, 0);

  const handleSubmit = async (formData: any) => {
    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate success/failure randomly (80% success rate)
      const isSuccess = Math.random() > 0.2;

      if (isSuccess) {
        // Clear cart on successful payment
        clearCart();
        navigate('/payment-success');
      } else {
        navigate('/payment-cancelled');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      navigate('/payment-cancelled');
    } finally {
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    navigate('/shop');
    return null;
  }

  return (
    <div className="container mx-auto p-0 md:p-24 max-w-1280">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
        {/* Order Summary */}
        <div>
          <BillingPlanPanel title={<Trans message="Order Summary" />}>
            <div className="space-y-16">
              {cart.map((item: CartItem) => {
                const basePrice = parseFloat(item.price[0]?.price || '0');
                const price = item.localCurrencyRate || basePrice;
                const currency = item.price[0]?.currency || 'GBP';
                const itemTotal = price * item.quantity;

                return (
                  <div key={item.id} className="flex items-center gap-12 p-12 border border-divider rounded">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-48 h-48 object-contain rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold">{item.name}</h4>
                      <p className="text-sm text-muted">Quantity: {item.quantity}</p>
                      <p className="text-sm font-medium">
                        {currency === 'GBP' ? '£' : '$'}{itemTotal.toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })}

              <div className="border-t border-divider pt-16">
                <div className="flex justify-between text-lg font-bold">
                  <span><Trans message="Total" />:</span>
                  <span className="text-primary">£{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </BillingPlanPanel>
        </div>

        {/* Payment Form */}
        <div>
          <BillingPlanPanel title={<Trans message="Payment Details" />}>
            <Form form={form} onSubmit={handleSubmit} className="space-y-20">
              {/* Contact Information */}
              <div className="space-y-16">
                <h3 className="text-lg font-semibold">
                  <Trans message="Contact Information" />
                </h3>
                <FormTextField
                  name="email"
                  type="email"
                  label={<Trans message="Email Address" />}
                  required
                />
                <FormTextField
                  name="name"
                  label={<Trans message="Full Name" />}
                  required
                />
              </div>

              {/* Payment Method */}
              <div className="space-y-16">
                <h3 className="text-lg font-semibold">
                  <Trans message="Payment Method" />
                </h3>
                <FormRadioGroup
                  name="paymentMethod"
                >
                  <Radio value="card" className="flex items-center gap-12 p-12 border border-divider rounded">
                    <CreditCardIcon size="md" />
                    <div>
                      <div className="font-medium"><Trans message="Credit/Debit Card" /></div>
                      <div className="text-sm text-muted"><Trans message="Visa, MasterCard, American Express" /></div>
                    </div>
                  </Radio>
                  <Radio value="paypal" className="flex items-center gap-12 p-12 border border-divider rounded">
                    <AccountBalanceWalletIcon size="md" />
                    <div>
                      <div className="font-medium">PayPal</div>
                      <div className="text-sm text-muted"><Trans message="Pay with your PayPal account" /></div>
                    </div>
                  </Radio>
                </FormRadioGroup>
              </div>

              {/* Card Details (shown when card is selected) */}
              {paymentMethod === 'card' && (
                <div className="space-y-16">
                  <FormTextField
                    name="cardNumber"
                    label={<Trans message="Card Number" />}
                    placeholder="1234 5678 9012 3456"
                    required
                  />
                  <div className="grid grid-cols-2 gap-12">
                    <FormTextField
                      name="expiryDate"
                      label={<Trans message="Expiry Date" />}
                      placeholder="MM/YY"
                      required
                    />
                    <FormTextField
                      name="cvv"
                      label={<Trans message="CVV" />}
                      placeholder="123"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Billing Address */}
              <div className="space-y-16">
                <h3 className="text-lg font-semibold">
                  <Trans message="Billing Address" />
                </h3>
                <FormTextField
                  name="address"
                  label={<Trans message="Address" />}
                  required
                />
                <div className="grid grid-cols-2 gap-12">
                  <FormTextField
                    name="city"
                    label={<Trans message="City" />}
                    required
                  />
                  <FormTextField
                    name="postcode"
                    label={<Trans message="Postcode" />}
                    required
                  />
                </div>
                <FormTextField
                  name="country"
                  label={<Trans message="Country" />}
                  required
                />
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start gap-8">
                <input
                  type="checkbox"
                  id="terms"
                  name="acceptTerms"
                  required
                  className="mt-4"
                />
                <label htmlFor="terms" className="text-sm text-muted">
                  <Trans message="I agree to the" />{' '}
                  <a href="#" className="text-primary hover:underline">
                    <Trans message="Terms of Service" />
                  </a>{' '}
                  <Trans message="and" />{' '}
                  <a href="#" className="text-primary hover:underline">
                    <Trans message="Privacy Policy" />
                  </a>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="flat"
                color="primary"
                className="w-full"
                size="lg"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Trans message="Processing Payment..." />
                ) : (
                  <Trans message={`Pay £${total.toFixed(2)}`} />
                )}
              </Button>
            </Form>
          </BillingPlanPanel>
        </div>
      </div>
    </div>
  );
}